import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Link2, Unlink } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Deliberate person picker: nothing links until the owner confirms a specific member. */
export default function LinkPersonDialog({ open, onOpenChange, contactId, onLinked }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setRows([]); setPending(null); return undefined; }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setRows([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/crm/link/search", { params: { q } });
        setRows(data.people || []);
      } catch { setRows([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, open]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/crm/contacts/${contactId}/link`, { user_id: pending.user_id });
      toast.success(`Linked to ${pending.display_name}`);
      onOpenChange(false);
      onLinked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "crm_link_already_used"
        ? "Another one of your contacts is already linked to this member"
        : "Couldn't link this contact");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="link-person-dialog">
        <DialogHeader>
          <DialogTitle>Link to a Startup Otter member</DialogTitle>
          <DialogDescription>
            Linking is private to you. They are never notified and can't see that you saved them.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus className="pl-9"
            aria-label="Search Startup Otter members" data-testid="link-search-input"
            placeholder="Search by name, headline or handle" maxLength={100} />
        </div>

        <ul className="max-h-72 space-y-1.5 overflow-y-auto" data-testid="link-results">
          {q.trim().length >= 2 && rows.length === 0 && (
            <li className="p-3 text-sm text-muted-foreground" data-testid="link-no-results">
              No members match that search.
            </li>
          )}
          {rows.map((p) => (
            <li key={p.user_id}>
              <button type="button" onClick={() => setPending(p)} disabled={!!p.linked_contact_id}
                data-testid={`link-result-${p.user_id}`}
                className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                  pending?.user_id === p.user_id ? "border-primary bg-primary/5"
                    : "border-border hover:bg-secondary/60"} ${p.linked_contact_id ? "opacity-50" : ""}`}>
                {p.headshot_url
                  ? <img src={p.headshot_url} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                  : <span className="h-9 w-9 shrink-0 rounded-full bg-secondary" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-[600]">{p.display_name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[p.headline, [p.city, p.state].filter(Boolean).join(", "),
                      p.handle && `@${p.handle}`].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {p.linked_contact_id && (
                  <span className="shrink-0 text-[11px] text-muted-foreground">Already linked</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {pending && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm"
            data-testid="link-confirm-box">
            Link this contact to <span className="font-[640]">{pending.display_name}</span>?
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPending(null)}
                data-testid="link-confirm-cancel">Not this person</Button>
              <Button size="sm" onClick={confirm} disabled={busy} data-testid="link-confirm">
                <Link2 className="mr-1.5 h-4 w-4" /> Confirm link
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UnlinkButton({ contactId, onUnlinked }) {
  const unlink = async () => {
    try {
      await api.delete(`/crm/contacts/${contactId}/link`);
      toast.success("Unlinked — both records kept");
      onUnlinked?.();
    } catch { toast.error("Couldn't unlink"); }
  };
  return (
    <Button variant="outline" size="sm" onClick={unlink} data-testid="contact-unlink">
      <Unlink className="mr-1.5 h-4 w-4" /> Unlink
    </Button>
  );
}
